import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  Inject,
  inject,
  input,
  output,
  PLATFORM_ID,
  signal
} from '@angular/core';
import {
  ADDICTION,
  AGE_0_5,
  AGE_13_17,
  AGE_6_12,
  ANXIETY,
  COGNITIVE_BEHAVIORAL_THERAPY,
  DEPRESSION,
  EMDR,
  FEMALE_GENDER,
  IFilterFormValues,
  INTERNAL_FAMILY_SYSTEMS,
  MALE_GENDER,
  MINDFULNESS,
  NON_BINARY_GENDER,
  PSYCHIATRIST_URL_TITLE,
  PSYCHODYNAMIC
} from '@sondermind/data-access/public-facing/filter-form';
import { IconSizes, IrisIconComponent } from '@sondermind/iris/icon';
import { CASH_PAY_OPTION_VALUE } from '@sondermind/utilities/models-provider-network-company';
import { IFindMyFitChip } from '../../models/chip.interface';

@Component({
  standalone: true,
  selector: 'find-my-fit',
  templateUrl: './find-my-fit.component.html',
  styleUrls: ['./find-my-fit.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IrisIconComponent, CommonModule],
})
export class FindMyFitComponent {
  private destroyRef = inject(DestroyRef);
  private elementRef = inject(ElementRef);

  readonly iconSizes = IconSizes;

  readonly filters = input<Partial<IFilterFormValues> | null>(null);
  readonly findMyFit = output<IFindMyFitChip[]>();
  readonly moduleViewed = output();
  readonly startButtonClicked = output<string[]>();

  private hasEmittedViewEvent = false;

  constructor(@Inject(PLATFORM_ID) private platformId: object) {
    afterNextRender(() => {
      this.setupVisibilityObserver();
    });
  }

  // looking for something is the most robust question/answer set, includes one question from each filter
  readonly lookingForSomethingElseChips = computed(() => {
    const chips = [] as IFindMyFitChip[];
    const filters = this.filters();

    // gender question
    const genders = filters?.genders ?? [];
    if (!genders.some((g) => g.tag === MALE_GENDER.tag)) {
      chips.push({ title: 'Male therapists', values: [MALE_GENDER], formKey: 'genders' });
    } else if (!genders.some((g) => g.tag === FEMALE_GENDER.tag)) {
      chips.push({ title: 'Female therapists', values: [FEMALE_GENDER], formKey: 'genders' });
    } else if (genders.some((g) => g.tag === NON_BINARY_GENDER.tag)) {
      chips.push({ title: 'Non-binary therapists ', values: [NON_BINARY_GENDER], formKey: 'genders' });
    }

    // insurance question
    chips.push({
      title: 'Search by insurance',
      values: [],
      startingPrompt: 'I am looking for a provider that accepts my insurance. Can you search based on my insurance?'
    });

    // specialties
    const specialties = filters?.specialties ?? [];
    if (!specialties.some((s) => s.tag === DEPRESSION.tag)) {
      chips.push({ title: 'Help with depression', values: [DEPRESSION], formKey: 'specialties' });
    } else if (!specialties.some((s) => s.tag === ANXIETY.tag)) {
      chips.push({ title: 'Help with anxiety', values: [ANXIETY], formKey: 'specialties' });
    } else {
      chips.push({ title: 'Help with addiction', values: [ADDICTION], formKey: 'specialties' });
    }

    // age
    const ages = filters?.ages ?? [];
    if (!ages.some((a) => a.tag === AGE_0_5.tag) || !ages.some((a) => a.tag === AGE_6_12.tag)) {
      chips.push({ title: 'Providers who work with children', values: [AGE_0_5, AGE_6_12], formKey: 'ages' });
    } else if (!ages.some((a) => a.tag === AGE_13_17.tag)) {
      chips.push({ title: 'Providers who work with adolescents', values: [AGE_13_17], formKey: 'ages' });
    }

    // psych
    if (filters?.serviceType?.tag !== PSYCHIATRIST_URL_TITLE) {
      chips.push({
        title: 'Medication management',
        values: [{
          title: PSYCHIATRIST_URL_TITLE,
          tag: PSYCHIATRIST_URL_TITLE
        }],
        formKey: 'serviceType'
      });
    }

    chips.push({ title: 'Something else', values: [] });

    return chips;
  });

  readonly treatmentPreferenceChips: IFindMyFitChip[] = [
    {
      title: COGNITIVE_BEHAVIORAL_THERAPY.title,
      values: [COGNITIVE_BEHAVIORAL_THERAPY],
      formKey: 'treatmentPreferences'
    },
    { title: EMDR.title, values: [EMDR], formKey: 'treatmentPreferences' },
    { title: MINDFULNESS.title, values: [MINDFULNESS], formKey: 'treatmentPreferences' },
    { title: INTERNAL_FAMILY_SYSTEMS.title, values: [INTERNAL_FAMILY_SYSTEMS], formKey: 'treatmentPreferences' },
    { title: PSYCHODYNAMIC.title, values: [PSYCHODYNAMIC], formKey: 'treatmentPreferences' },
    {
      title: 'Explain these to me',
      values: [],
      startingPrompt: 'Can you explain the different treatment preferences and therapy approaches to me?'
    },
  ];

  readonly insuranceChips: IFindMyFitChip[] = [
    {
      title: "None - I'll be paying out-of-pocket",
      values: [CASH_PAY_OPTION_VALUE],
      singleSelect: true,
      formKey: 'insurance'
    },
    {
      title: 'Cigna',
      values: [],
      startingPrompt: 'I have Cigna insurance. Can you help me find providers that accept Cigna?',
      singleSelect: true
    },
    {
      title: 'Anthem',
      values: [],
      startingPrompt: 'I have Anthem insurance. Can you help me find providers that accept Anthem?',
      singleSelect: true
    },
    {
      title: 'Medicare',
      values: [],
      startingPrompt: 'I have Medicare. Can you help me find providers that accept Medicare?',
      singleSelect: true
    },
    {
      title: 'United Healthcare',
      values: [],
      startingPrompt:
        'I have United Healthcare insurance. Can you help me find providers that accept United Healthcare?',
      singleSelect: true
    },
    {
      title: 'Kaiser Permanente',
      values: [],
      startingPrompt:
        'I have Kaiser Permanente insurance. Can you help me find providers that accept Kaiser Permanente?',
      singleSelect: true
    },
    {
      title: 'Something else',
      values: [],
      startingPrompt: 'I have insurance and would like help finding providers that accept my plan.'
        + ' Can you guide me through adding my insurance?',
      singleSelect: true
    },
  ];

  readonly displayedChipGroup = computed(() => {
    const filters = this.filters();
    if (filters && !filters.insurance) {
      return {
        title: "Let's find a provider who takes your insurance. What insurance do you have?",
        subtitle: 'SonderMind accepts hundreds of insurance plans.',
        chips: this.insuranceChips
      };
    }
    if (filters && !filters.treatmentPreferences?.length) {
      return {
        title: 'What treatment approach are you looking for?',
        subtitle: null,
        chips: this.treatmentPreferenceChips
      };
    }
    return {
      title: 'Looking for something different?',
      subtitle: null,
      chips: this.lookingForSomethingElseChips()
    };
  });

  readonly selectedChips = signal<Set<IFindMyFitChip>>(new Set());

  toggleChip(chip: IFindMyFitChip): void {
    this.selectedChips.update((current) => {
      const updated = new Set(current);
      if (updated.has(chip)) {
        updated.delete(chip);
        return updated;
      }
      if (chip.singleSelect) {
        updated.forEach((selected) => {
          if (selected.singleSelect) updated.delete(selected);
        });
      }
      updated.add(chip);
      return updated;
    });
  }

  isChipSelected(chip: IFindMyFitChip): boolean {
    return this.selectedChips().has(chip);
  }

  onFindMyFitClicked(): void {
    const selectedChipsArray = [...this.selectedChips()];
    const selectedPillTitles = selectedChipsArray.map((chip) => chip.title);

    this.startButtonClicked.emit(selectedPillTitles);
    this.findMyFit.emit(selectedChipsArray);
  }

  private setupVisibilityObserver(): void {
    if (!isPlatformBrowser(this.platformId) || this.hasEmittedViewEvent) {
      return;
    }

    try {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !this.hasEmittedViewEvent) {
              this.hasEmittedViewEvent = true;
              this.moduleViewed.emit();
              observer.disconnect();
            }
          });
        },
        { threshold: 0.1 }
      );

      observer.observe(this.elementRef.nativeElement);

      this.destroyRef.onDestroy(() => {
        observer.disconnect();
      });
    } catch (err) {
      // Silently fail if IntersectionObserver is not supported
    }
  }
}
